import { useQuery, useQueryClient } from '@tanstack/react-query';

import * as Api from 'contracts/api';
import { UseExposedQueryOptions } from 'queries/types';
import { getErrorMessage } from 'queries/utils';
import { createDeferred } from 'utils/create-deferred';

enum Status {
    DONE = 200,
    STARTED = 202,
    IN_PROGRESS = 204,
    CAPTCHA_REQUIRED = 402
}

export class CaptchaRequiredError extends Error {}
export class TimeoutError extends Error {}
export class PromtFailureError extends Error {}
export class UnexpectedError extends Error {}

interface Parameters extends Api.V4.Bulletins.AiInsights.Payload {
    interval?: number;
    timeout?: number;
}

export function useAiInsights<TData = Api.V4.Bulletins.AiInsights.Response>(
    { id, interval = 5_000, timeout = 60_000 }: Parameters,
    options?: UseExposedQueryOptions<Api.V4.Bulletins.AiInsights.Response, Error, TData>
) {
    const queryClient = useQueryClient();

    return useQuery<Api.V4.Bulletins.AiInsights.Response, Error, TData>({
        ...options,
        queryKey: Api.V4.Bulletins.AiInsights.QueryKey({ id }),
        queryFn: async ({ signal }) => {
            const deferred = createDeferred<Api.V4.Bulletins.AiInsights.Response>();
            const timers = { poll: NaN, timeout: NaN };

            const poll = async (previousStatus: number) => {
                const response = await fetch(`${Api.V4.Bulletins.AiInsights.Endpoint()}?id=${id}`, {
                    signal,
                    headers: { ['Accept']: 'application/json' }
                });

                const currentStatus = response.status;

                if (currentStatus === Status.CAPTCHA_REQUIRED) {
                    document.dispatchEvent(new Event('show-captcha'));
                    return deferred.reject(new CaptchaRequiredError());
                }

                if (currentStatus === Status.STARTED && previousStatus === Status.IN_PROGRESS) {
                    return deferred.reject(new PromtFailureError());
                }

                if (currentStatus === Status.DONE) {
                    await queryClient.invalidateQueries({ queryKey: Api.V4.Bulletins.AiInsightsCounter.QueryKey() });
                    const json = await response.json();
                    if (json.result && 'insight' in json.result) {
                        return deferred.resolve(json.result);
                    } else {
                        return deferred.reject(new UnexpectedError(getErrorMessage(json)));
                    }
                }

                timers.poll = window.setTimeout(() => poll(currentStatus), interval);
            };

            timers.timeout = window.setTimeout(() => deferred.reject(new TimeoutError()), timeout);
            void poll(0);

            return deferred.promise.finally(() => {
                Object.values(timers).forEach((timer) => clearTimeout(timer));
            });
        }
    });
}
